import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, mangaList } from '@/mocks/manga';

const sortOptions = [
  { value: 'popular', label: '最受欢迎' },
  { value: 'latest', label: '最近更新' },
  { value: 'rating', label: '评分最高' },
  { value: 'newest', label: '最新上架' },
];

const siteUrl = 'https://hanmanwu.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('popular');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryName = currentCategory.name;

  const filteredManga = useMemo(() => {
    let list = mangaList.filter((m) => m.categorySlug === slug);

    if (statusFilter === 'ongoing') list = list.filter((m) => m.status === '连载中');
    if (statusFilter === 'completed') list = list.filter((m) => m.status === '已完结');

    if (sortBy === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'latest') list = [...list].sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    if (sortBy === 'newest') list = [...list];
    if (sortBy === 'popular') list = [...list].sort((a, b) => parseInt(b.views) - parseInt(a.views));

    return list;
  }, [slug, sortBy, statusFilter]);

  const categoryKeywords: Record<string, string> = {
    romance: '恋爱漫画,甜蜜恋爱,韩漫恋爱题材',
    school: '校园漫画,青春校园,韩漫校园题材',
    fantasy: '奇幻漫画,魔法冒险,韩漫奇幻题材',
    action: '热血漫画,战斗冒险,韩漫热血题材',
    mystery: '悬疑漫画,推理侦探,韩漫悬疑题材',
    comedy: '搞笑漫画,轻松幽默,韩漫搞笑题材',
    historical: '古风漫画,古代传奇,韩漫古风题材',
    urban: '都市漫画,现代都市,韩漫都市题材',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`韩漫屋 - ${categoryName}漫画 | 免费正版韩国${categoryName}漫画在线阅读`}
        description={`韩漫屋${categoryName}漫画专区，提供海量免费正版韩国${categoryName}漫画在线阅读。每日更新热门${categoryName}韩漫，高清画质，优质阅读体验。`}
        keywords={`韩漫屋,${categoryName}漫画,韩国${categoryName}漫画,免费${categoryName}韩漫,${categoryKeywords[slug || ''] || ''}`}
        canonicalUrl={`${siteUrl}/category/${slug}`}
      />

      <main>
        <Navbar />
        <div className="pt-20">
          <div className="w-full px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类', href: '/category/romance' },
                  { label: categoryName },
                ]}
              />
            </div>

            {/* Category Tabs */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                <span className="w-10 h-10 inline-flex items-center justify-center bg-primary-100 rounded-lg mr-3">
                  <i className={`${currentCategory.icon} text-lg text-primary-500`}></i>
                </span>
                {categoryName}漫画
              </h1>

              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">状态：</span>
                {[
                  { value: 'all', label: '全部' },
                  { value: 'ongoing', label: '连载中' },
                  { value: 'completed', label: '已完结' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      statusFilter === opt.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-background-100 border border-background-200/70 rounded-lg px-3 py-1.5 text-sm text-foreground-700 outline-none cursor-pointer"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Manga Grid */}
            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group flex flex-col"
                    data-product-shop
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={manga.coverImage}
                        alt={manga.title}
                        title={`${manga.title} - ${categoryName}韩漫`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          manga.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-foreground-700 text-background-50'
                        }`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span className="text-xs text-white font-medium">{manga.rating}</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{manga.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{manga.views} 阅读</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {manga.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </span>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
              </div>
            )}

            {/* Pagination */}
            {filteredManga.length > 0 && (
              <div className="flex items-center justify-center gap-2 py-12">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 cursor-pointer hover:bg-primary-100 hover:text-primary-500 transition-colors">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {[1, 2, 3].map((page) => (
                  <button
                    key={page}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                      page === 1 ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 cursor-pointer hover:bg-primary-100 hover:text-primary-500 transition-colors">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}